/* TASK-217 / TASK-260: discover a parked MCP OAuth consent after sign-in.

   /oauth-mcp/authorize without a session parks the authorize request server-
   side (mcp-worker: OAUTH_KV entry + __Host-mcp-oauth-pending cookie, 15 min)
   and sends the browser to /login or /signup. Only the email+password forms
   bring the user back on their own (they keep ?next=): Google returns to the
   app root, /confirm and MFA land on /app, and onboarding takes over from
   there. Ask the API once per authenticated user whether a consent is pending,
   then store its validated hand-back URL in PendingOAuthContext. The context
   releases one guarded transition after billing or a server-granted
   Free continuation allows it. The consent itself remains explicit.

   Fail-open: any error (network, 4xx/5xx, malformed payload, the cookie not
   being sent cross-site on a preview host) leaves the user in the app exactly
   as before. */
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePendingOAuth } from '../contexts/PendingOAuthContext.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';
import { api } from '../services/api/client.js';
import {
  consentAttemptStorageKey,
  pendingOAuthSnapshot,
  validatedPendingConsent,
} from '../lib/pendingOAuth.js';

export default function PendingOAuthResume() {
  const { setPending, showManualPending, setPersonalPending } = usePendingOAuth();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const userId = user?.id;
  const stayIntentOwnerRef = useRef(null);
  let stayRequested = false;
  try {
    stayRequested = new URLSearchParams(location.search).get('mcp_stay') === '1';
  } catch { /* malformed search cannot express Stay */ }
  if (stayRequested && stayIntentOwnerRef.current == null) stayIntentOwnerRef.current = userId;
  const honorStay = stayRequested && stayIntentOwnerRef.current === userId;

  useEffect(() => {
    if (userId == null) return undefined;
    let cancelled = false;
    setPending(null);
    (async () => {
      try {
        const payload = await api.auth.getPendingOAuth();
        if (cancelled) return;
        const latest = pendingOAuthSnapshot(payload, userId);
        if (payload?.pending === false || latest?.connector === 'chatgpt') {
          setPersonalPending?.('chatgpt', latest, userId);
        }
        if (!honorStay) {
          setPending(latest);
          return;
        }

        // Stay is only a suppression hint. A fresh, user-bound, allowlisted,
        // unexpired pending response is still required before showing a manual
        // continuation or writing its SHA-256 attempt identity.
        let markerKey = null;
        if (validatedPendingConsent(latest, { userId })) {
          try {
            markerKey = await consentAttemptStorageKey(latest, userId);
          } catch { /* in-memory automatic suppression below is sufficient */ }
        }
        if (cancelled || !validatedPendingConsent(latest, { userId })) {
          if (cancelled) return;
        } else if (markerKey) {
          try { window.localStorage.setItem(markerKey, '1'); } catch { /* honor Stay in memory */ }
        }
        if (!showManualPending(latest, userId)) return;

        const params = new URLSearchParams(location.search);
        params.delete('mcp_stay');
        const search = params.toString();
        navigate({
          pathname: location.pathname,
          search: search ? `?${search}` : '',
          hash: location.hash || '',
        }, { replace: true });
      } catch {
        /* fail-open into the app */
      }
    })();
    return () => { cancelled = true; };
  }, [setPending, showManualPending, setPersonalPending, userId, user?.emailVerified]);

  // Discover the other host before choosing an automatic ChatGPT return. A
  // failed/malformed read must not be mistaken for an absent Claude request.
  useEffect(() => {
    if (userId == null || !setPersonalPending) return undefined;
    let cancelled = false;
    api.auth.getPendingOAuth({ connector: 'claude' }).then(payload => {
      if (cancelled) return;
      const latest = pendingOAuthSnapshot(payload, userId);
      if (payload?.pending === false || latest?.connector === 'claude') {
        setPersonalPending('claude', latest, userId);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [userId, user?.emailVerified, setPersonalPending]);

  return null;
}
